import { format as originalFormat, startOfDay, subHours } from 'date-fns';
import add from 'date-fns/add';
import * as React from 'react';
import { memo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import { AccessPermissions, useQueryGetGetFlights, useQueryGetGetResources } from '../../backend';
import { useCurrentUserClient } from '../../contexts/OidcContext/OidcContext.selectors';
import { useBoolean } from '../../hooks/useBoolean';
import { errorToastConfig } from '../../utils/utils';
import { ganttDayFormat } from '../Rms/RmsContent/Gantt/gantt.constants';
import { RmsContent } from '../Rms/RmsContent/RmsContent';
import { useRmsGanttState, useRmsGanttValue, useSetRmsGantt } from '../Rms/RmsContent/RmsContext';
import { RmsHeader } from '../Rms/RmsHeader/RmsHeader';
import { resourcesPollingInterval } from './ResourcesPage';
import { StyledRmsBox } from './RmsPage.style';

export const ganttPollingInterval = 15 * 1000;

export const RmsPage = memo(function RmsPage() {
    const setResourcesByType = useSetRmsGantt('resourcesByType');
    const resourceType = useRmsGanttValue('resourceType');
    const [day, setDay] = useRmsGanttState('day');
    const client = useCurrentUserClient();
    const {
        value: shallGanttAutoScroll,
        setFalse: disableGanttAutoScroll,
        setTrue: enableGanttAutoScroll,
    } = useBoolean(true);

    useEffect(() => {
        const permissions = Array.from(client?.permissions);
        const newDay = subHours(new Date(), client?.startDayHourShift || 0);
        if (permissions.includes(AccessPermissions.TACTICAL_MANAGEMENT)) {
            setDay(startOfDay(newDay));
        } else {
            // default, day = J + 1
            setDay(add(startOfDay(newDay), { days: 1 }));
        }
    }, [client?.permissions, client?.startDayHourShift, setDay]);

    const { data: allResources } = useQueryGetGetResources(
        {
            includeNonActive: true,
        },
        {
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
            select: response => {
                return {
                    ...response,
                    data: response.data.sort((a, b) => {
                        return a.name.localeCompare(b.name, undefined, { numeric: true });
                    }),
                };
            },
            refetchOnMount: true,
            refetchInterval: resourcesPollingInterval,
            notifyOnChangeProps: ['data', 'error'],
        },
    );

    useEffect(() => {
        setResourcesByType(allResources?.filter(r => r.type === resourceType));
    }, [allResources, resourceType, setResourcesByType]);

    useQueryGetGetFlights(
        { selectedDay: originalFormat(day, ganttDayFormat), selectedResourceType: resourceType },
        {
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
            refetchInterval: ganttPollingInterval,
            refetchOnMount: true,
            enabled: !!resourceType,
        },
    );

    return (
        <StyledRmsBox>
            <RmsHeader enableGanttAutoScroll={enableGanttAutoScroll} />
            <RmsContent shallGanttAutoScroll={shallGanttAutoScroll} disableGanttAutoScroll={disableGanttAutoScroll} />
        </StyledRmsBox>
    );
});
